import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    analyticsSection1,
    analyticsSection2,
    analyticsTopProjects,
    analyticsTopSupplier,
    analyticsUserByDevice,
    analyticsMonthlyTrend,
    analyticsDailyTrend,
    analyticsRdMonthlyTrend,
    analyticsRdDailyTrend,
} from '../Api/Dashboard';
import { changeNumberFormat } from '../Common/NumberConversion';
import Footer from './Layout/Footer';
import Header from './Layout/Header';
import { CChart } from '@coreui/react-chartjs';
import '../styles/dashboard/dashboard.css';
import {
    DEFAULT_DATE_PRESET,
    DATE_PRESET_OPTIONS,
    buildDashboardDateParams,
    buildDateSearchParams,
    getDateRangeKey,
    getPresetDateRange,
    parseDateRangeFromSearchParams,
} from '../utils/dashboardDateRange';

const THEME_COLORS = ['#405189', '#0ab39c', '#299cdb', '#f7b84b', '#f06548'];
const CHART_HEIGHT = 300;
const BAR_TOP_RADIUS = { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 };

const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const baseChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 400 },
    interaction: { mode: 'index', intersect: false },
    layout: { padding: { top: 4, bottom: 4 } },
    plugins: {
        legend: {
            position: 'bottom',
            align: 'center',
            labels: {
                boxWidth: 10,
                boxHeight: 10,
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 16,
                color: '#878a99',
                font: { size: 12, family: 'inherit' },
            },
        },
        tooltip: {
            enabled: true,
            backgroundColor: '#fff',
            titleColor: '#495057',
            bodyColor: '#878a99',
            borderColor: '#e9ebec',
            borderWidth: 1,
            padding: 12,
            boxPadding: 4,
        },
    },
};

const barChartOptions = {
    ...baseChartOptions,
    datasets: {
        bar: {
            barPercentage: 0.65,
            categoryPercentage: 0.7,
            maxBarThickness: 36,
            borderRadius: BAR_TOP_RADIUS,
            borderSkipped: false,
        },
    },
    scales: {
        x: {
            grid: { display: false, drawBorder: false },
            ticks: { color: '#878a99', font: { size: 11 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
        },
        y: {
            beginAtZero: true,
            grid: { color: '#f3f4f7', drawBorder: false },
            ticks: { color: '#878a99', font: { size: 11 }, precision: 0 },
        },
    },
};

const lineChartOptions = {
    ...baseChartOptions,
    elements: {
        line: { tension: 0.35, borderWidth: 2 },
        point: { radius: 2, hoverRadius: 4, borderWidth: 2 },
    },
    scales: {
        x: {
            grid: { display: false, drawBorder: false },
            ticks: { color: '#878a99', font: { size: 10 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 10 },
        },
        y: {
            beginAtZero: true,
            grid: { color: '#f3f4f7', drawBorder: false },
            ticks: { color: '#878a99', font: { size: 11 }, precision: 0 },
        },
    },
};

const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
        legend: { display: false },
        tooltip: baseChartOptions.plugins.tooltip,
    },
};

const INITIAL_LOADING = {
    section1: true,
    section2: true,
    section2Full: false,
    userByDevice: true,
    topSupplier: true,
    topProject: true,
    trends: true,
    rdTrends: true,
};

function useInView() {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return undefined;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);
                observer.disconnect();
            }
        }, { rootMargin: '120px' });

        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return [ref, inView];
}

function DashboardToolbar({
    draftPreset,
    draftFrom,
    draftTo,
    isApplying,
    onPresetSelect,
    onFromChange,
    onToChange,
    onApplyCustom,
}) {
    const isCustom = draftPreset === 'custom';

    return (
        <div className="dashboard-toolbar">
            <select
                className="form-select form-select-sm dashboard-period-select"
                value={draftPreset}
                onChange={(e) => onPresetSelect(e.target.value)}
                disabled={isApplying}
                aria-label="Date range"
            >
                {DATE_PRESET_OPTIONS.filter((option) => option.id !== 'custom').map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                ))}
                <option value="custom">Custom range</option>
            </select>
            <label className="dashboard-toolbar__date-field">
                <span className="dashboard-toolbar__date-label">From</span>
                <input
                    type="date"
                    className="form-control form-control-md"
                    value={draftFrom}
                    onChange={(e) => onFromChange(e.target.value)}
                    disabled={!isCustom || isApplying}
                    readOnly={!isCustom}
                    aria-label="Start date"
                />
            </label>
            <span className="dashboard-toolbar__sep" aria-hidden="true">to</span>
            <label className="dashboard-toolbar__date-field">
                <span className="dashboard-toolbar__date-label">To</span>
                <input
                    type="date"
                    className="form-control form-control-md"
                    value={draftTo}
                    onChange={(e) => onToChange(e.target.value)}
                    disabled={!isCustom || isApplying}
                    readOnly={!isCustom}
                    aria-label="End date"
                />
            </label>
            {isCustom ? (
                <button
                    type="button"
                    className="btn btn-sm btn-primary dashboard-toolbar__apply"
                    onClick={onApplyCustom}
                    disabled={isApplying}
                >
                    {isApplying ? 'Applying...' : 'Apply'}
                </button>
            ) : null}
        </div>
    );
}

function StatCardSkeleton() {
    return (
        <div className="dashboard-metric-col">
            <div className="card dashboard-stat-card h-100">
                <div className="card-body">
                    <div className="dashboard-skeleton dashboard-stat-skeleton" />
                    <div className="dashboard-skeleton dashboard-stat-skeleton-value" />
                    <div className="dashboard-skeleton dashboard-stat-skeleton-badge" />
                </div>
            </div>
        </div>
    );
}

function StatCard({ sec2 }) {
    const growth = sec2?.growthDetails;
    return (
        <div className="dashboard-metric-col">
            <div className="card dashboard-stat-card h-100">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="min-w-0">
                            <p className="dashboard-stat-label text-truncate">{sec2.title}</p>
                            <h2 className="dashboard-stat-value mb-0">
                                {sec2?.value}
                            </h2>
                        </div>
                        <div className="dashboard-stat-icon">
                            <i className={`ri-${sec2.iconType}`}></i>
                        </div>
                    </div>
                    {growth ? (
                        <div className="dashboard-stat-growth mt-3">
                            <span className={growth?.isPositive ? 'dashboard-growth-badge is-up' : 'dashboard-growth-badge is-down'}>
                                <i className={growth?.isPositive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
                                {growth?.value}{growth?.valueType === 'percentage' ? '%' : ''}
                            </span>
                            <span className="dashboard-growth-label">vs. {growth?.label}</span>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function ChartSkeleton() {
    return <div className="dashboard-skeleton dashboard-chart-skeleton" />;
}

function DashboardChart({ type, data, options, loading, ready, emptyLabel = 'No data available', compact = false }) {
    const [canRender, setCanRender] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (loading || !ready) {
            setCanRender(false);
            return undefined;
        }

        const frame = requestAnimationFrame(() => {
            setCanRender(true);
        });

        return () => cancelAnimationFrame(frame);
    }, [loading, ready]);

    useEffect(() => {
        if (!canRender || !wrapRef.current) return undefined;

        const triggerResize = () => window.dispatchEvent(new Event('resize'));
        const observer = new ResizeObserver(triggerResize);
        observer.observe(wrapRef.current);
        triggerResize();

        return () => observer.disconnect();
    }, [canRender, type, data]);

    const hasData = data?.labels?.length > 0 && data?.datasets?.some((set) => set.data?.length > 0);
    const wrapClass = compact ? 'dashboard-doughnut-wrap' : 'dashboard-chart-wrap';

    return (
        <div ref={wrapRef} className={wrapClass}>
            {loading || !canRender ? (
                <ChartSkeleton />
            ) : !hasData ? (
                <div className="dashboard-chart-empty">{emptyLabel}</div>
            ) : (
                <CChart
                    type={type}
                    data={data}
                    options={options}
                    customTooltips={false}
                    height={compact ? 220 : CHART_HEIGHT}
                />
            )}
        </div>
    );
}

function TrendsPanel({ trendGroup, setTrendGroup, trendPeriod, setTrendPeriod, activeChart }) {
    return (
        <div className="card dashboard-trends-panel" id="dashboard-trends">
            <div className="card-header border-0">
                <ul className="dashboard-trend-tabs">
                    <li>
                        <button
                            type="button"
                            className={trendGroup === 'response' ? 'is-active' : ''}
                            onClick={() => setTrendGroup('response')}
                        >
                            Response
                        </button>
                    </li>
                    <li>
                        <button
                            type="button"
                            className={trendGroup === 'rd' ? 'is-active' : ''}
                            onClick={() => setTrendGroup('rd')}
                        >
                            RD Failures
                        </button>
                    </li>
                </ul>
                <div className="dashboard-period-toggle" role="group" aria-label="Trend period">
                    <button
                        type="button"
                        className={trendPeriod === 'monthly' ? 'is-active' : ''}
                        onClick={() => setTrendPeriod('monthly')}
                    >
                        Monthly
                    </button>
                    <button
                        type="button"
                        className={trendPeriod === 'daily' ? 'is-active' : ''}
                        onClick={() => setTrendPeriod('daily')}
                    >
                        Daily
                    </button>
                </div>
            </div>
            <div className="card-body pt-2">
                <DashboardChart
                    key={`${trendGroup}-${trendPeriod}`}
                    type={activeChart.type}
                    data={activeChart.data}
                    options={activeChart.options}
                    loading={activeChart.loading}
                    ready={activeChart.ready}
                    emptyLabel={activeChart.emptyLabel}
                />
            </div>
        </div>
    );
}

function Dashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const appliedKeyRef = useRef('');
    const insightsInViewRef = useRef(false);

    useEffect(() => {
        document.title = 'Dashboard - Mindforce';
    }, []);

    const [section1, setSection1] = useState();
    const [section2, setSection2] = useState();
    const [sectionUserByDevice, setSectionUserByDevice] = useState();
    const [sectionTopSupplier, setSectionTopSupplier] = useState();
    const [topSupplierTotalResponse, setTopSupplierTotalResponse] = useState();
    const [topSupplierIndividualResponse, setTopSupplierIndividaualResponse] = useState([]);
    const [sectionTopProject, setSectionTopProject] = useState();
    const [monthlyTrend, setMonthlyTrend] = useState([]);
    const [dailyTrend, setDailyTrend] = useState([]);
    const [rdMonthlyTrend, setRdMonthlyTrend] = useState([]);
    const [rdDailyTrend, setRdDailyTrend] = useState([]);
    const [loading, setLoading] = useState(INITIAL_LOADING);
    const [showAllMetrics, setShowAllMetrics] = useState(false);
    const [fullMetricsLoaded, setFullMetricsLoaded] = useState(false);
    const [isApplyingDate, setIsApplyingDate] = useState(false);
    const initialDateRange = useMemo(
        () => parseDateRangeFromSearchParams(searchParams),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const [appliedDateRange, setAppliedDateRange] = useState(initialDateRange);
    const [draftPreset, setDraftPreset] = useState(initialDateRange.preset);
    const [draftFrom, setDraftFrom] = useState(initialDateRange.from);
    const [draftTo, setDraftTo] = useState(initialDateRange.to);
    const [trendGroup, setTrendGroup] = useState('response');
    const [trendPeriod, setTrendPeriod] = useState('daily');
    const [insightsRef, insightsInView] = useInView();
    insightsInViewRef.current = insightsInView;

    const setLoaded = useCallback((key) => {
        setLoading((prev) => ({ ...prev, [key]: false }));
    }, []);

    const getDateParams = useCallback((dateRange) => (
        buildDashboardDateParams(dateRange)
    ), []);

    const loadSection2 = useCallback(async (dateParams, scope) => {
        try {
            const res = await analyticsSection2({ ...dateParams, scope });
            if (res.status === 1) {
                setSection2(res.payload);
                if (scope === 'full') {
                    setFullMetricsLoaded(true);
                }
            }
            return res;
        } finally {
            setLoading((prev) => ({
                ...prev,
                section2: false,
                section2Full: false,
            }));
        }
    }, []);

    const loadDashboardData = useCallback(async (dateRange, options = {}) => {
        const { metricsScope = 'top', includeInsights = false } = options;
        const dateParams = getDateParams(dateRange);

        setLoading({
            section1: true,
            section2: true,
            section2Full: metricsScope === 'full',
            userByDevice: includeInsights,
            topSupplier: includeInsights,
            topProject: includeInsights,
            trends: true,
            rdTrends: true,
        });

        const section1Promise = analyticsSection1(dateParams).then((res) => {
            if (res.status === 1) setSection1(res.payload);
            setLoaded('section1');
        });

        const section2Promise = loadSection2(dateParams, metricsScope);

        const insightPromises = [];
        if (includeInsights) {
            insightPromises.push(
                analyticsUserByDevice(dateParams).then((res) => {
                    if (res.status === 1) setSectionUserByDevice(res.payload);
                    setLoaded('userByDevice');
                }),
                analyticsTopSupplier(dateParams).then((res) => {
                    if (res.status === 1) {
                        const individual = res.payload?.details?.map((ele) => toNumber(ele?.value)) || [];
                        setSectionTopSupplier(res.payload);
                        setTopSupplierTotalResponse(changeNumberFormat(res.payload?.totalResponses?.toString() || '0'));
                        setTopSupplierIndividaualResponse(individual);
                    }
                    setLoaded('topSupplier');
                }),
                analyticsTopProjects(dateParams).then((res) => {
                    if (res.status === 1) setSectionTopProject(res.payload);
                    setLoaded('topProject');
                })
            );
        }

        const trendsPromise = Promise.all([
            analyticsMonthlyTrend(dateParams),
            analyticsDailyTrend(dateParams),
        ]).then(([monthlyRes, dailyRes]) => {
            if (monthlyRes.status === 1) setMonthlyTrend(monthlyRes.payload || []);
            if (dailyRes.status === 1) setDailyTrend(dailyRes.payload || []);
            setLoaded('trends');
        });

        const rdTrendsPromise = Promise.all([
            analyticsRdMonthlyTrend(dateParams),
            analyticsRdDailyTrend(dateParams),
        ]).then(([monthlyRes, dailyRes]) => {
            if (monthlyRes.status === 1) setRdMonthlyTrend(monthlyRes.payload || []);
            if (dailyRes.status === 1) setRdDailyTrend(dailyRes.payload || []);
            setLoaded('rdTrends');
        });

        await Promise.all([
            section1Promise,
            section2Promise,
            ...insightPromises,
            trendsPromise,
            rdTrendsPromise,
        ]);
    }, [getDateParams, loadSection2, setLoaded]);

    useEffect(() => {
        const hasDateParams = searchParams.get('preset') || (searchParams.get('from') && searchParams.get('to'));
        if (!hasDateParams) {
            const defaultRange = { preset: DEFAULT_DATE_PRESET, ...getPresetDateRange(DEFAULT_DATE_PRESET) };
            setSearchParams(buildDateSearchParams(defaultRange), { replace: true });
            return;
        }

        const urlRange = parseDateRangeFromSearchParams(searchParams);
        const key = getDateRangeKey(urlRange);
        if (key === appliedKeyRef.current) {
            return;
        }
        appliedKeyRef.current = key;

        setAppliedDateRange(urlRange);
        setDraftPreset(urlRange.preset);
        setDraftFrom(urlRange.from);
        setDraftTo(urlRange.to);
        setShowAllMetrics(false);
        setFullMetricsLoaded(false);
        setIsApplyingDate(true);

        if (['today', 'last_7_days'].includes(urlRange.preset)) {
            setTrendPeriod('daily');
        } else if (urlRange.preset === 'last_3_months' || urlRange.preset === 'last_month') {
            setTrendPeriod('monthly');
        }

        loadDashboardData(urlRange, {
            metricsScope: 'top',
            includeInsights: insightsInViewRef.current,
        }).finally(() => {
            setIsApplyingDate(false);
        });
    }, [searchParams, setSearchParams, loadDashboardData]);

    useEffect(() => {
        if (!insightsInView) return undefined;
        const dateParams = getDateParams(appliedDateRange);
        let cancelled = false;

        setLoading((prev) => ({
            ...prev,
            userByDevice: true,
            topSupplier: true,
            topProject: true,
        }));

        analyticsUserByDevice(dateParams).then((res) => {
            if (!cancelled && res.status === 1) setSectionUserByDevice(res.payload);
            if (!cancelled) setLoaded('userByDevice');
        });

        analyticsTopSupplier(dateParams).then((res) => {
            if (!cancelled && res.status === 1) {
                const individual = res.payload?.details?.map((ele) => toNumber(ele?.value)) || [];
                setSectionTopSupplier(res.payload);
                setTopSupplierTotalResponse(changeNumberFormat(res.payload?.totalResponses?.toString() || '0'));
                setTopSupplierIndividaualResponse(individual);
            }
            if (!cancelled) setLoaded('topSupplier');
        });

        analyticsTopProjects(dateParams).then((res) => {
            if (!cancelled && res.status === 1) setSectionTopProject(res.payload);
            if (!cancelled) setLoaded('topProject');
        });

        return () => { cancelled = true; };
    }, [insightsInView, appliedDateRange, getDateParams, setLoaded]);

    const applyDateRange = useCallback((nextRange) => {
        const nextKey = getDateRangeKey(nextRange);
        if (nextKey === appliedKeyRef.current) {
            return;
        }
        setSearchParams(buildDateSearchParams(nextRange));
    }, [setSearchParams]);

    const handlePresetSelect = (presetId) => {
        setDraftPreset(presetId);
        if (presetId === 'custom') {
            return;
        }
        const nextRange = { preset: presetId, ...getPresetDateRange(presetId) };
        applyDateRange(nextRange);
    };

    const handleApplyCustomRange = () => {
        applyDateRange({ preset: 'custom', from: draftFrom, to: draftTo });
    };

    const handleToggleAllMetrics = async () => {
        if (showAllMetrics) {
            setShowAllMetrics(false);
            setSection2((prev) => (prev || []).slice(0, 5));
            return;
        }

        setShowAllMetrics(true);
        if (fullMetricsLoaded) return;

        setLoading((prev) => ({ ...prev, section2: true, section2Full: true }));
        const dateParams = getDateParams(appliedDateRange);
        await loadSection2(dateParams, 'full');
    };

    useEffect(() => {
        if (loading.section2 || loading.trends) return undefined;
        const timer = setTimeout(() => window.dispatchEvent(new Event('resize')), 150);
        return () => clearTimeout(timer);
    }, [loading.section2, loading.trends, loading.rdTrends, showAllMetrics, trendGroup, trendPeriod]);

    const deviceChartData = useMemo(() => ({
        labels: sectionUserByDevice?.map((item) => item.deviceType) || [],
        datasets: [{
            data: sectionUserByDevice?.map((item) => toNumber(item.value)) || [],
            backgroundColor: THEME_COLORS,
            borderWidth: 2,
            borderColor: '#fff',
            hoverOffset: 6,
        }],
    }), [sectionUserByDevice]);

    const monthlyChartData = useMemo(() => ({
        labels: monthlyTrend.map((item) => item.monthName),
        datasets: [
            {
                label: 'Click',
                backgroundColor: THEME_COLORS[0],
                data: monthlyTrend.map((item) => toNumber(item.clicks)),
            },
            {
                label: 'Complete',
                backgroundColor: THEME_COLORS[1],
                data: monthlyTrend.map((item) => toNumber(item.responses)),
            },
        ],
    }), [monthlyTrend]);

    const dailyChartData = useMemo(() => ({
        labels: dailyTrend.map((item) => item.dateName),
        datasets: [
            {
                label: 'Click',
                backgroundColor: 'rgba(64, 81, 137, 0.12)',
                borderColor: THEME_COLORS[0],
                pointBackgroundColor: THEME_COLORS[0],
                pointBorderColor: '#fff',
                fill: true,
                data: dailyTrend.map((item) => toNumber(item.clicks)),
            },
            {
                label: 'Complete',
                backgroundColor: 'rgba(10, 179, 156, 0.12)',
                borderColor: THEME_COLORS[1],
                pointBackgroundColor: THEME_COLORS[1],
                pointBorderColor: '#fff',
                fill: true,
                data: dailyTrend.map((item) => toNumber(item.responses)),
            },
        ],
    }), [dailyTrend]);

    const rdMonthlyChartData = useMemo(() => ({
        labels: rdMonthlyTrend.map((item) => item.monthName),
        datasets: [
            {
                label: 'Click',
                backgroundColor: THEME_COLORS[2],
                data: rdMonthlyTrend.map((item) => toNumber(item.clicks)),
            },
            {
                label: 'Failure',
                backgroundColor: THEME_COLORS[4],
                data: rdMonthlyTrend.map((item) => toNumber(item.failures)),
            },
        ],
    }), [rdMonthlyTrend]);

    const rdDailyChartData = useMemo(() => ({
        labels: rdDailyTrend.map((item) => item.dateName),
        datasets: [
            {
                label: 'Click',
                backgroundColor: 'rgba(41, 156, 219, 0.12)',
                borderColor: THEME_COLORS[2],
                pointBackgroundColor: THEME_COLORS[2],
                pointBorderColor: '#fff',
                fill: true,
                data: rdDailyTrend.map((item) => toNumber(item.clicks)),
            },
            {
                label: 'Failure',
                backgroundColor: 'rgba(240, 101, 72, 0.12)',
                borderColor: THEME_COLORS[4],
                pointBackgroundColor: THEME_COLORS[4],
                pointBorderColor: '#fff',
                fill: true,
                data: rdDailyTrend.map((item) => toNumber(item.failures)),
            },
        ],
    }), [rdDailyTrend]);

    const activeTrendChart = useMemo(() => {
        if (trendGroup === 'response') {
            if (trendPeriod === 'monthly') {
                return {
                    type: 'bar',
                    data: monthlyChartData,
                    options: barChartOptions,
                    loading: loading.trends,
                    ready: !loading.trends,
                    emptyLabel: 'No monthly response data',
                };
            }
            return {
                type: 'line',
                data: dailyChartData,
                options: lineChartOptions,
                loading: loading.trends,
                ready: !loading.trends,
                emptyLabel: 'No daily response data',
            };
        }

        if (trendPeriod === 'monthly') {
            return {
                type: 'bar',
                data: rdMonthlyChartData,
                options: barChartOptions,
                loading: loading.rdTrends,
                ready: !loading.rdTrends,
                emptyLabel: 'No monthly RD failure data',
            };
        }
        return {
            type: 'line',
            data: rdDailyChartData,
            options: lineChartOptions,
            loading: loading.rdTrends,
            ready: !loading.rdTrends,
            emptyLabel: 'No daily RD failure data',
        };
    }, [
        trendGroup,
        trendPeriod,
        monthlyChartData,
        dailyChartData,
        rdMonthlyChartData,
        rdDailyChartData,
        loading.trends,
        loading.rdTrends,
    ]);

    const visibleMetrics = section2 || [];
    const metricsLoadingCount = showAllMetrics ? 6 : 5;

    return (
        <div>
            <Header />
            <div id="layout-wrapper">
                <div className="main-content">
                    <div className="page-content">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-12 col-lg-12 col-xl-12 col-md-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between gap-3 flex-wrap">
                                        <div>
                                        <h4 className="mb-sm-0">Dashboard</h4>
                                        {!loading.section1 ? (
                                            <p className="dashboard-summary mb-0 mt-1">
                                                {section1?.response?.text}
                                                {section1?.response?.link?.text ? (
                                                    <>
                                                        ,{' '}
                                                        <a href={section1?.response?.link?.url} target="_blank" rel="noopener noreferrer">
                                                            {section1?.response?.link?.text}
                                                            <i className="mdi mdi-arrow-right ms-1"></i>
                                                        </a>
                                                    </>
                                                ) : null}
                                            </p>
                                        ) : (
                                            <div className="dashboard-skeleton mt-2" style={{ height: 14, width: 260 }} />
                                        )}
                                        </div>
                                        <div className="dashboard-header-actions d-flex align-items-start gap-2 flex-wrap">
                                            <DashboardToolbar
                                                draftPreset={draftPreset}
                                                draftFrom={draftFrom}
                                                draftTo={draftTo}
                                                isApplying={isApplyingDate}
                                                onPresetSelect={handlePresetSelect}
                                                onFromChange={setDraftFrom}
                                                onToChange={setDraftTo}
                                                onApplyCustom={handleApplyCustomRange}
                                            />
                                            {section1?.button?.map((btn, idx) => (
                                                <a
                                                    key={btn?.url || idx}
                                                    href={btn?.url}
                                                    className={btn?.btnType}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {btn?.text}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div id="dashboard-metrics" className="dashboard-metrics-section mt-3">
                            <div className={`dashboard-metrics-grid${showAllMetrics ? ' is-expanded' : ''}`}>
                                {loading.section2 ? (
                                    Array.from({ length: metricsLoadingCount }).map((_, i) => (
                                        <StatCardSkeleton key={`metric-skeleton-${i}`} />
                                    ))
                                ) : (
                                    visibleMetrics.map((sec2, i) => (
                                        <StatCard key={sec2?.title || sec2?.key || i} sec2={sec2} />
                                    ))
                                )}
                            </div>
                            {!loading.section2 && (
                                <div className="text-center mt-2 mb-1">
                                    <button
                                        type="button"
                                        className="dashboard-view-all-link"
                                        onClick={handleToggleAllMetrics}
                                        disabled={loading.section2Full}
                                    >
                                        {loading.section2Full
                                            ? 'Loading metrics...'
                                            : showAllMetrics
                                                ? 'Show fewer metrics'
                                                : 'View all metrics'}
                                    </button>
                                </div>
                            )}
                            </div>

                            <div className="mt-4">
                                    <TrendsPanel
                                        trendGroup={trendGroup}
                                        setTrendGroup={setTrendGroup}
                                        trendPeriod={trendPeriod}
                                        setTrendPeriod={setTrendPeriod}
                                        activeChart={activeTrendChart}
                                    />
                            </div>

                            <div className="row mt-4 dashboard-insights-row" id="dashboard-insights" ref={insightsRef}>
                                {!insightsInView ? (
                                    <>
                                        {Array.from({ length: 3 }).map((_, i) => (
                                            <div className="col-xl-4 col-md-6 mb-3" key={`insight-placeholder-${i}`}>
                                                <div className="card dashboard-insights-placeholder">
                                                    <div className="card-body">
                                                        <div className="dashboard-skeleton mb-3" style={{ height: 16, width: '45%' }} />
                                                        <div className="dashboard-skeleton" style={{ height: 220, width: '100%' }} />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    <>
                                        <div className="col-xl-4 col-md-6 mb-3">
                                            <div className="card card-height-100 dashboard-top-projects">
                                                <div className="card-header align-items-center d-flex border-0 pb-0" style={{ backgroundColor: 'transparent' }}>
                                                    <h4 className="card-title mb-0 flex-grow-1">Top Projects</h4>
                                                </div>
                                                <div className="card-body pt-2">
                                                    <div className="top-projects-grid top-projects-header">
                                                        <span>Project</span>
                                                        <span className="text-end">Clicks</span>
                                                        <span className="text-end">CR</span>
                                                    </div>
                                                    {loading.topProject ? (
                                                        Array.from({ length: 6 }).map((_, i) => (
                                                            <div className="px-2 py-2" key={`project-skel-${i}`}>
                                                                <div className="dashboard-skeleton" style={{ height: 14, width: '100%' }} />
                                                            </div>
                                                        ))
                                                    ) : sectionTopProject?.length > 0 ? (
                                                        <ul className="list-unstyled mb-0">
                                                            {sectionTopProject.map((project, index) => {
                                                                const projectUrl = project?.hasChild
                                                                    ? `/group-project-child-detail?id=${project?.id}`
                                                                    : `/single-project-detail?id=${project?.id}`;
                                                                const projectLabel = project?.projectCode
                                                                    ? `${project.projectCode} | ${project?.name || 'N/A'}`
                                                                    : (project?.name || 'N/A');

                                                                return (
                                                                <li key={project?.id || index} className="top-projects-grid top-projects-row">
                                                                    <a
                                                                        href={projectUrl}
                                                                        className="top-projects-link"
                                                                        title={projectLabel}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        {project?.projectCode ? (
                                                                            <span className="top-projects-code">{project.projectCode}</span>
                                                                        ) : null}
                                                                        <span className="top-projects-name-text">
                                                                            {project?.name || 'N/A'}
                                                                        </span>
                                                                    </a>
                                                                    <span className="top-projects-metric text-end text-muted">
                                                                        {project?.clicks ?? 0}
                                                                    </span>
                                                                    <span className={`top-projects-metric text-end ${(project?.responses?.value ?? 0) > 0 ? 'text-success fw-medium' : 'text-muted'}`}>
                                                                        {project?.responses?.value ?? 0}
                                                                        {project?.responses?.valueType === 'percentage' ? '%' : ''}
                                                                    </span>
                                                                </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    ) : (
                                                        <div className="text-center text-muted py-4 fs-13">No project data available</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-xl-4 col-md-6 mb-3">
                                            <div className="card card-height-100">
                                                <div className="card-header align-items-center d-flex" style={{ backgroundColor: 'transparent' }}>
                                                    <h4 className="card-title mb-0 flex-grow-1">Users by Device</h4>
                                                </div>
                                                <div className="card-body">
                                                    <DashboardChart
                                                        type="doughnut"
                                                        data={deviceChartData}
                                                        options={doughnutOptions}
                                                        loading={loading.userByDevice}
                                                        ready={!loading.userByDevice}
                                                        emptyLabel="No device data available"
                                                        compact
                                                    />

                                                    <div className="table-responsive mt-3">
                                                        <table className="table table-borderless table-sm table-centered align-middle table-nowrap mb-0">
                                                            <tbody className="border-0">
                                                                {loading.userByDevice ? (
                                                                    Array.from({ length: 4 }).map((_, i) => (
                                                                        <tr key={`device-skel-${i}`}>
                                                                            <td colSpan={3}>
                                                                                <div className="dashboard-skeleton" style={{ height: 14, width: '80%' }} />
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    sectionUserByDevice?.map((userByDevice, i) => {
                                                                        const gDet = userByDevice?.growthDetails;
                                                                        return (
                                                                            <tr key={userByDevice?.deviceType || i}>
                                                                                <td>
                                                                                    <h4 className="text-truncate fs-14 fs-medium mb-0">
                                                                                        <i className="ri-stop-fill align-middle fs-18 me-2" style={{ color: THEME_COLORS[i % THEME_COLORS.length] }}></i>
                                                                                        {userByDevice?.deviceType}
                                                                                    </h4>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-muted mb-0">{userByDevice?.value}</p>
                                                                                </td>
                                                                                <td className="text-end">
                                                                                    <p className={gDet?.isPositive ? 'text-success fw-medium fs-12 mb-0' : 'text-danger fw-medium fs-12 mb-0'}>
                                                                                        <i className={gDet?.isPositive ? 'ri-arrow-up-s-fill fs-5 align-middle' : 'ri-arrow-down-s-fill fs-5 align-middle'}></i>
                                                                                        {gDet?.value}{gDet?.valueType === 'percentage' ? '%' : ''}
                                                                                    </p>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-xl-4 col-md-6 mb-3">
                                            <div className="card card-height-100">
                                                <div className="card-header align-items-center d-flex" style={{ backgroundColor: 'transparent' }}>
                                                    <h4 className="card-title mb-0 flex-grow-1">Top Suppliers</h4>
                                                    <div className="flex-shrink-0">
                                                        {sectionTopSupplier?.isExportReportAllowed ? (
                                                            <button type="button" className="btn btn-soft-primary btn-sm">
                                                                Export Report
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                </div>
                                                <div className="card-body">
                                                    {loading.topSupplier ? (
                                                        <>
                                                            <div className="dashboard-skeleton mb-3" style={{ height: 14, width: '50%' }} />
                                                            <div className="dashboard-skeleton mb-3" style={{ height: 28, width: '35%' }} />
                                                            <div className="dashboard-skeleton mb-4" style={{ height: 10, width: '100%', borderRadius: 20 }} />
                                                            {Array.from({ length: 4 }).map((_, i) => (
                                                                <div className="dashboard-skeleton mb-2" key={`supplier-skel-${i}`} style={{ height: 12, width: '90%' }} />
                                                            ))}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="row align-items-center">
                                                                <div className="col-6">
                                                                    <h6 className="text-muted text-uppercase fw-semibold text-truncate fs-12 mb-3">Total responses</h6>
                                                                    <h4 className="mb-0">{topSupplierTotalResponse}</h4>
                                                                    <p className="mb-0 mt-2 text-muted">
                                                                        <span className={sectionTopSupplier?.growthDetails?.isPositive ? 'badge badge-soft-success mb-0' : 'badge badge-soft-danger mb-0'}>
                                                                            <i className={sectionTopSupplier?.growthDetails?.isPositive ? 'ri-arrow-up-line align-middle' : 'ri-arrow-down-line align-middle'}></i>
                                                                            {sectionTopSupplier?.growthDetails?.value}
                                                                            {sectionTopSupplier?.growthDetails?.valueType === 'percentage' ? '%' : ''}
                                                                        </span>
                                                                        {' '}vs. {sectionTopSupplier?.growthDetails?.label}
                                                                    </p>
                                                                </div>
                                                                <div className="col-6">
                                                                    <div className="text-center">
                                                                        <img src={sectionTopSupplier?.imageSrc} className="img-fluid" alt="" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="mt-3 pt-2">
                                                                <div className="progress progress-lg rounded-pill">
                                                                    {sectionTopSupplier?.details?.map((detail, i) => (
                                                                        <div
                                                                            className="progress-bar"
                                                                            role="progressbar"
                                                                            key={detail?.name || i}
                                                                            style={{ width: `${topSupplierIndividualResponse[i]}%`, backgroundColor: detail?.color }}
                                                                            aria-valuenow={topSupplierIndividualResponse[i]}
                                                                            aria-valuemin="0"
                                                                            aria-valuemax="100"
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            <div className="mt-3 pt-2">
                                                                {sectionTopSupplier?.details?.map((detail) => (
                                                                    <div className="d-flex mb-2" key={detail?.name}>
                                                                        <div className="flex-grow-1">
                                                                            <p className="text-truncate text-muted fs-14 mb-0">
                                                                                <i className="mdi mdi-circle align-middle me-2" style={{ color: detail?.color }}></i>
                                                                                {detail?.name}
                                                                            </p>
                                                                        </div>
                                                                        <div className="flex-shrink-0">
                                                                            <p className="mb-0">
                                                                                {detail?.value}{detail?.valueType === 'percentage' ? '%' : ''}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            {sectionTopSupplier?.isShowAllBtnActive ? (
                                                                <div className="mt-2 text-center">
                                                                    <a href="/supplier" className="text-muted text-decoration-underline" target="_blank" rel="noopener noreferrer">Show All</a>
                                                                </div>
                                                            ) : null}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default Dashboard;
